<script lang="ts" setup>
import { onKeyStroke } from '@vueuse/core';
import { computed, useSlots, watch } from 'vue';

import { KeyboardKeyCode } from '../../models';
import AppButton from '../appButton/AppButton.vue';
import AppIconButton from '../appIconButton/AppIconButton.vue';
import AppOverlay from '../modals/AppOverlay.vue';
import { AppFadeInOutTransition } from '../transitions';
import AppSlideTransition from '../transitions/appSlideTransition/AppSlideTransition.vue';

export interface AppSlidePanelProps {
  direction: 'left' | 'right' | 'top' | 'bottom' | 'disabled';
  isOpen: boolean;
  closeButtonAriaLabel: string;
  shouldShowCloseButton?: boolean;
  width?: string;
  padding?: string;
  shouldHideOverlay?: boolean;
  disableHeader?: boolean;
  isButtonDisabled?: boolean;
  isSmallDevice?: boolean;
}

const props = withDefaults(defineProps<AppSlidePanelProps>(), {
  width: 'auto',
  padding: 'var(--spacing-16)',
});

export interface AppStandardModalEmits {
  (eventName: 'close'): void;
  (eventName: 'confirm'): void;
}

const emit = defineEmits<AppStandardModalEmits>();

let stopKeyListener: (() => void) | null = null;

watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      stopKeyListener = onKeyStroke(KeyboardKeyCode.Escape, () =>
        emit('close')
      );
    } else if (stopKeyListener) {
      stopKeyListener();
      stopKeyListener = null;
    }
  }
);

const slots = useSlots();

const menuDirectionClass = computed(() => `slide-${props.direction}`);
const hasButton = computed(() => !!slots['button']);
const hasSummary = computed(() => !!slots['summary']);
const hasFooter = computed(() => !!slots['footer']);
const doneButtonSize = computed<'medium' | 'large'>(() =>
  props.isSmallDevice ? 'medium' : 'large'
);
</script>

<template>
  <div class="app-slide-panel">
    <AppSlideTransition :direction="props.direction">
      <div v-if="isOpen" class="slide-panel" :class="menuDirectionClass">
        <header v-if="!props.disableHeader" class="slide-header">
          <div class="main-info">
            <div class="title">
              <slot name="title" />
            </div>
          </div>
          <AppIconButton
            v-if="props.shouldShowCloseButton"
            type="secondary"
            state="enabled"
            size="medium"
            :ariaLabel="props.closeButtonAriaLabel"
            :icon="{ name: 'close' }"
            @click="emit('close')"
          />
        </header>
        <div class="slide-body">
          <slot name="body" />
        </div>
        <footer
          v-if="hasButton || hasSummary || hasFooter"
          class="slide-footer"
          :class="{
            left: hasSummary,
          }"
        >
          <AppButton
            v-if="hasButton"
            :state="props.isButtonDisabled ? 'disabled' : 'enabled'"
            class="button"
            type="primary"
            :size="doneButtonSize"
            @click="emit('confirm')"
          >
            <slot name="button" />
          </AppButton>
          <div v-else-if="hasSummary">
            <slot name="summary" />
          </div>
          <slot name="footer" />
        </footer>
      </div>
    </AppSlideTransition>
    <AppFadeInOutTransition v-if="!props.shouldHideOverlay">
      <AppOverlay v-if="isOpen" :is-modal="false" @click="emit('close')" />
    </AppFadeInOutTransition>
  </div>
</template>

<style lang="scss" scoped>
@import '../../styles/_fonts.scss';
.app-slide-panel {
  .slide-panel {
    position: fixed;
    background-color: var(--color-white);
    display: flex;
    flex-direction: column;
    z-index: var(--panel-first-level-z-index);

    .slide-header {
      display: flex;
      justify-content: space-between;
      padding: var(--spacing-16);
      .main-info {
        display: flex;
        flex-direction: column;
        justify-content: center;

        .title {
          color: var(--color-neutral-70);
          @include font-label-xl-bold;
        }
      }
    }

    .slide-body {
      position: relative;
      padding: 0 v-bind(padding) v-bind(padding);
      overflow: auto;
      flex-grow: 1;
      margin: 0;
    }

    .slide-footer {
      display: flex;
      justify-content: center;

      padding: var(--spacing-16);
      border-top: 1px solid var(--color-neutral-30);

      .button {
        min-width: 300px;
      }
    }
  }
  .slide-left,
  .slide-right,
  .slide-disabled {
    top: 0;
    height: 100%;
    width: v-bind(width);
  }
  .slide-left {
    left: 0;
  }

  .slide-right,
  .slide-disabled {
    right: 0;
  }
  .slide-top,
  .slide-bottom {
    left: 0;
    width: 100%;
  }
  .slide-top {
    top: 0;
  }

  .slide-bottom {
    bottom: 0;
  }
}
</style>
